<template>
    <div>
        <redesign-base-chart
            ref="chartRef"
            :widget="data"
            :option="chartOption"
            class="w-full h-full"
            @on-update-legend="updateVisibleSeries"
        />
    </div>
</template>

<script setup lang="ts">
import useQueues from '@/composables/useQueues'
import { DEFAULT_CHART_SERIES_LINES_KEYS, getAllSeries } from '@/enum/queueConfigs'
import { useExtensions } from '@/composables/useExtensions'
import { HOLD_STATUS, Statuses } from '@/enum/redesignStatusTypes'
import { computed, onBeforeUnmount, onMounted, ref, shallowRef, toRef } from 'vue'
import { getDashboardGMTConfig, getGmtOffset } from '@/api/widgetDataApi'
import { GmtOffsetTypes } from '@/enum/gmt'
import { WidgetExtended } from '@/types/dashboard'
import { selectedAccountData } from '@/composables/globalData/useEntitiesData'
import { getActiveLayoutColors } from '@/composables/globalData/useLayouts'
import RedesignBaseChart from '@/components/RedesignComponents/Charts/RedesignBaseChart.vue'
import cloneDeep from 'lodash/cloneDeep'
import type { LegendComponentOption } from 'echarts/components'
import { timeFormatter } from '@/helpers/timeFormatter'
import { CallbackDataParams } from 'echarts/types/dist/shared'
import get from 'lodash/get'
import { ComponentExposed } from 'vue-component-type-helpers'
import type { ChartOptions } from '@/types/echart'

interface ChartSerie {
    value: number
    agents: string[]
    statusName: string | number
    color: string
    key?: string | number
    type: string
}

interface ChartSerieData {
    name: string | number
    data: [number, number][]
    agents: string[]
    color?: string
    type: string
    key: string | number
}
interface CustomData {
    value: number | [number, number]
    agents: string[]
}

type selectedSeriesType = Record<string | number, boolean>
interface FormatterParams extends CallbackDataParams {
    data: CustomData
}

interface LastTooltip {
    dataIndex?: number
    seriesIndex?: number
    html: string
}

const MAX_POINTS = 12
const timeOut = 5000
const queueKey = DEFAULT_CHART_SERIES_LINES_KEYS.QUEUE_CALLS.key
const maxWaitTimeKey = DEFAULT_CHART_SERIES_LINES_KEYS.MAX_WAITING_TIME.key

const props = defineProps<{
    data: WidgetExtended
}>()

const emit = defineEmits<{
    (event: 'on-update', widgetData: WidgetExtended): void
}>()

const { queues: allQueues } = useQueues()
const { extensions: agents } = useExtensions()

const lastTooltip = shallowRef<LastTooltip>({
    html: ''
})
const widgetData = toRef(props, 'data')
let intervalId: ReturnType<typeof setInterval> | null = null
const chartOption = shallowRef<ChartOptions>({})
const seriesDataMap = shallowRef<Record<string, ChartSerieData>>({})
const chartRef = ref<ComponentExposed<typeof RedesignBaseChart>>()
// Local state for legend visibility - persists across data updates without saving to widget
const localLegendSelected = ref<selectedSeriesType | null>(null)

//Computed
const dashboardGMTConfig = computed(() => getDashboardGMTConfig())
const dashboardGMTOffset = computed(() => getGmtOffset())

const allSeries = computed(() => {
    return getAllSeries(selectedAccountData.value?.AccountBreaks)
})

const visibleSeries = computed(() => {
    if (widgetData.value.WidgetLayout?.showSeries) {
        return props.data.WidgetLayout.showSeries
    }
    // Hide logged out status by default
    return allSeries.value
        .filter(serie => serie.value !== Statuses.LOGOUT)
        .map(serie => serie.value)
})

const visibleQueues = computed(() => widgetData.value.WidgetLayout?.showQueues)

const filteredQueues = computed(() => {
    if (visibleQueues.value && visibleQueues.value?.length) {
        return allQueues.value.filter(queue => visibleQueues.value?.includes(queue.QueueID))
    }

    return allQueues.value
})

const currentLayoutColors = computed(() => getActiveLayoutColors())

const defaultChartSeries = Object.values(DEFAULT_CHART_SERIES_LINES_KEYS).reduce<Record<string, ChartSerie>>((acc, item) => {
    acc[item.key] = {
        value: 0,
        agents: [],
        statusName: item.name as string,
        color: item?.layoutColorVar && currentLayoutColors.value[item.layoutColorVar]
            ? currentLayoutColors.value[item.layoutColorVar]
            : get(widgetData.value, `WidgetLayout.colors.${item?.colorVar}`, item.colorDefault),
        type: item.serieType === 'column' ? 'bar' : item.serieType,
        key: item.key
    }
    return acc
}, {})

const statusesBaseDataStructure = computed(() => {
    const baseStatusesConfig = selectedAccountData.value?.AccountBreaks.reduce<Record<string | number, ChartSerie>>((acc, status) => {
        acc[status.StatusID] = {
            value: 0,
            agents: [],
            statusName: status.Name,
            color: status.ColorCode,
            type: 'bar',
            key: status.StatusID
        }
        return acc
    }, {})


    return {
        ...baseStatusesConfig,
        ...defaultChartSeries
    }
})

const selectedSeries = computed(() => {
    // If user has toggled legend, use local state
    if (localLegendSelected.value) {
        return localLegendSelected.value
    }

    // Otherwise, use initial visibility from widget config
    const selectedMap: selectedSeriesType = {}
    Object.values(statusesBaseDataStructure.value).forEach(item => {
        selectedMap[item.statusName] = visibleSeries.value?.includes(item.key) ?? false
    })
    return selectedMap
})

//Methods
function updateChartData () {
    if (!agents.value.length) return

    let newTime = new Date()

    if (dashboardGMTConfig.value.Type === GmtOffsetTypes.Account || dashboardGMTConfig.value.Type === GmtOffsetTypes.DashboardTimezone) {
        const offsetMs = newTime.getTimezoneOffset() * 60 * 1000
        newTime = new Date(newTime.getTime() + offsetMs + (dashboardGMTOffset.value ?? 0) * 1000)
    }

    const currentTime = newTime.getTime()
    const agentsInStatuses = cloneDeep(statusesBaseDataStructure.value)

    agents.value.forEach(agent => {
        let statusID: string | number = agent.representativeStatus
        if (agent.calls.length > 0) {
            if (agent.calls.some(call => call.answered && call.callstatus.toLowerCase() !== HOLD_STATUS)) {
                statusID = DEFAULT_CHART_SERIES_LINES_KEYS.AGENTS_IN_CALL.key
            }


            if (agent.calls.some(call => call.answered && call.callstatus.toLowerCase() === HOLD_STATUS)) {
                statusID = DEFAULT_CHART_SERIES_LINES_KEYS.AGENTS_ON_HOLD.key
            }
        }
        if (agentsInStatuses[statusID]) {
            agentsInStatuses[statusID].agents.push(agent.userName)
            agentsInStatuses[statusID].value += 1
        }
    })

    const queueCalls = []

    let minJoin = currentTime
    filteredQueues?.value?.forEach(queue => {
        queue.Calls?.forEach(call => {
            if (call) {
                queueCalls.push(call)
            }
            if (minJoin > call.JoinTimeStamp_UTC_CLIENT) {
                minJoin = call.JoinTimeStamp_UTC_CLIENT
            }
        })
    })

    agentsInStatuses[queueKey].value = queueCalls.length
    agentsInStatuses[maxWaitTimeKey].value = queueCalls.length > 0 ? (Math.floor(newTime.getTime()) - minJoin) / 1000 : 0

    Object.entries(agentsInStatuses).forEach(([ statusID, statusData ]) => {
        if (!seriesDataMap.value[statusID]) {
            seriesDataMap.value[statusID] = {
                name: statusData.statusName,
                key: statusID,
                agents: [],
                data: [],
                color: statusData.color,
                type: statusData.type
            }
        }

        seriesDataMap.value[statusID].data.push([ currentTime, statusData.value ])
        seriesDataMap.value[statusID].agents = statusData.agents
    })


    Object.values(seriesDataMap.value).forEach(series => {
        if (series.data.length > MAX_POINTS) {
            series.data.splice(0, series.data.length - MAX_POINTS)
        }
    })

    const { legends, dynamicTop } = buildLegends(seriesDataMap.value, selectedSeries.value, chartRef.value?.getChartWidth() || 400)

    const firstSeriesData = Object.values(seriesDataMap.value)[0]?.data || []
    const timeCategories = firstSeriesData.map(([ timestamp ]) => {
        const date = new Date(timestamp)
        return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}:${date.getSeconds().toString().padStart(2, '0')}`
    })

    chartOption.value = {
        tooltip: {
            trigger: 'item',
            formatter: (params: FormatterParams) => getTooltip(params)
        },
        legend: legends,
        xAxis: {
            type: 'category',
            data: timeCategories,
            axisLabel: {
                interval: 0,
                hideOverlap: true,
            },
        },
        yAxis: [
            {
                type: 'value',
                name: '',
                position: 'right',
            },
            {
                type: 'value',
                name: '',
                position: 'left',
                axisLabel: {
                    formatter: '{value} s'
                }
            }

        ],
        grid: {
            top: dynamicTop,
            bottom: 40,
            left: 15,
            right: 15,
            containLabel: true
        },
        series: Object.entries(seriesDataMap.value).map(([ id, serieData ]) => {
            const isLine = serieData.type === 'line'
            return {
                name: serieData.name,
                type: serieData.type,
                stack: isLine ? '' : 'total',
                barMaxWidth: isLine ? undefined : 40,
                // For line series, use simple values; for bar series, include agents for tooltip
                data: serieData.data.map(([ , y ]) => isLine
                    ? y
                    : {
                        value: y,
                        agents: serieData.agents
                    }
                ),
                itemStyle: serieData.color ? { color: serieData.color } : '',
                emphasis: { focus: 'series' },
                yAxisIndex: id === maxWaitTimeKey ? 1 : 0
            }
        }),
    }
    lastTooltip.value = {
        html: ''
    }
}

function chunkArray <T> (arr: T[], chunkSize: number): T[][] {
    const chunks: T[][] = []
    for (let i = 0; i < arr.length; i += chunkSize) {
        chunks.push(arr.slice(i, i + chunkSize))
    }
    return chunks
}

function buildLegends (seriesDataMap: Record<string, ChartSerieData>, selectedSeries: selectedSeriesType, widgetWidth: number, maxColumns = 5) {
    const legendsData = Object.values(seriesDataMap)
    const approxLegendItemWidth = 150
    let columns = Math.floor(widgetWidth / approxLegendItemWidth)

    columns = Math.max(1, Math.min(columns, maxColumns))

    const chunked = chunkArray(legendsData, Math.ceil(legendsData.length / columns))
    const maxRows = Math.max(...chunked.map(c => c.length))
    const rowHeight = 20
    const dynamicTop = 40 + (rowHeight * maxRows)

    const legends = chunked.map((chunk, i) => ({
        orient: 'vertical',
        left: i === 0 ? 15 : `${(100 / columns) * (i)}%`,
        top: 0,
        data: chunk,
        selected: selectedSeries
    }))

    return {
        legends,
        dynamicTop
    }
}
const updateVisibleSeries = (param: LegendComponentOption) => {
    // Update local legend state only - don't save to widget
    // This ensures legend changes persist across data updates without sending API requests
    if (param?.selected) {
        localLegendSelected.value = { ...param.selected }
    }
}

const  generateTooltip = (params: FormatterParams) => {
    const { seriesType, seriesName, data, value } = params
    // Line series have simple values, bar series have {value, agents} objects
    const count = typeof value === 'number'
        ? value
        : (typeof data?.value === 'number' ? data.value : (data?.value?.[1] ?? 0))

    if (seriesName === DEFAULT_CHART_SERIES_LINES_KEYS.MAX_WAITING_TIME.name) {
        return timeFormatter(count) + ` ${seriesName}`
    }

    // For line series (queue calls), show simple tooltip
    if (seriesType === 'line') {
        return `${seriesName}: ${count}`
    }

    // For bar series, show agents info
    const allSelectedAgentsInStatuses = countAgents(params.dataIndex)
    const agentsCount = data?.agents?.length ?? 0
    const percentageValue = allSelectedAgentsInStatuses > 0
        ? ((agentsCount / allSelectedAgentsInStatuses) * 100).toFixed(1) + '%'
        : '0%'

    const baseTooltip = `
    <div style="color:${params.color}">${seriesName}</div>
    <div>${agentsCount} of ${allSelectedAgentsInStatuses} - <span>${percentageValue}</span></div>
    `

    if (seriesType === 'bar' && data?.agents?.length) {
        const list = `
          <ul style="margin:6px 0 0;padding-left:10px;">
            ${data.agents.map((a: string) => `<li>- ${a}</li>`).join('')}
          </ul>`
        return baseTooltip + list
    }

    return baseTooltip
}


function getTooltip (params: FormatterParams) {
    if (
        lastTooltip.value.dataIndex !== params.dataIndex ||
        lastTooltip.value.seriesIndex !== params.seriesIndex
    ) {
        lastTooltip.value = {
            dataIndex: params.dataIndex,
            seriesIndex: params.seriesIndex,
            html: generateTooltip(params)
        }
    }
    return lastTooltip.value.html
}

function countAgents (dataIndex: number) {
    return Object.values(seriesDataMap.value).reduce((acc, item) => {
        if (selectedSeries.value[item.name] && item.type === 'bar') {
            acc += item.data[dataIndex][1]
        }
        return acc

    }, 0)
}

/*use if send request on legend click*/
// const updateWidget = (widgetData: WidgetExtended) => {
//     emit('on-update', widgetData)
// }

onMounted(() => {
    updateChartData()
    intervalId = setInterval(updateChartData, timeOut)
})
onBeforeUnmount(() => {
    if (intervalId) {
        clearInterval(intervalId)
        intervalId = null
    }
})
</script>

<style scoped lang="scss">

</style>
